import React, { useState, useEffect } from 'react';
import type { Category, CategoryCreate, CategoryUpdate, CustomFieldConfig } from '../../types';
import { CategoryCustomFields } from './CategoryCustomFields';
import { useAssetTypes } from '../../hooks/useAssetTypes';
import { useCreateCategory, useUpdateCategory } from '../../hooks/useCategories';

interface CategoryFormProps {
  category?: Category;
  onSubmit: () => void;
  onCancel: () => void;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [customFields, setCustomFields] = useState<CustomFieldConfig[]>([]);
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});

  const { data: assetTypes = [], isLoading: loadingAssetTypes } = useAssetTypes();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();

  const isLoading = createCategory.isLoading || updateCategory.isLoading;

  useEffect(() => {
    if (category) {
      setName(category.name);
      setDescription(category.description || '');
      setCustomFields(category.customFields || []);
    }
  }, [category]);

  const validateForm = (): boolean => {
    const newErrors: { name?: string; description?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'El nombre es requerido';
    } else if (name.length > 100) {
      newErrors.name = 'El nombre no puede exceder 100 caracteres';
    } else if (!/^[A-ZÁÉÍÓÚÑ0-9\s\-_&\.]+$/i.test(name)) {
      newErrors.name = 'El nombre contiene caracteres no válidos';
    }

    if (description && description.length > 500) {
      newErrors.description = 'La descripción no puede exceder 500 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (category) {
        // Update
        const data: CategoryUpdate = {
          name: name.trim(),
          description: description.trim() || undefined,
          customFields: customFields,
        };
        await updateCategory.mutateAsync({
          categoryId: category.categoryId,
          data,
        });
      } else {
        // Create
        const data: CategoryCreate = {
          name: name.trim(),
          description: description.trim() || undefined,
        };
        await createCategory.mutateAsync(data);
      }
      onSubmit();
    } catch (error) {
      // Error is handled by the hook
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Nombre <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Ej: FERRETERIA"
          disabled={isLoading}
          maxLength={100}
        />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Descripción
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Descripción opcional de la categoría"
          rows={3}
          disabled={isLoading}
          maxLength={500}
        />
        {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
        <p className="mt-1 text-sm text-gray-500">{description.length}/500 caracteres</p>
      </div>

      {/* Only show custom fields configuration when editing */}
      {category && !loadingAssetTypes && (
        <div className="border-t pt-6">
          <CategoryCustomFields
            customFields={customFields}
            assetTypes={assetTypes}
            onChange={setCustomFields}
            disabled={isLoading}
          />
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isLoading ? 'Guardando...' : category ? 'Actualizar' : 'Crear'}
        </button>
      </div>
    </form>
  );
};

export default CategoryForm;
